/**
 * The §3.1 pocket visual state machine, as an explicit, pure function.
 *
 * Kept out of `src/domain/` because it is a view concern, and out of the Wheel
 * component because the precedence rules are worth testing on their own.
 */

import type { PocketId } from '~/domain/types';

export type PocketVisualState =
  | 'hover-remove'
  | 'hover-add-new'
  | 'hover-add-overlap'
  | 'placed-heavy'
  | 'placed'
  | 'placed-light'
  | 'selected'
  | 'base';

/** Ordered highest-precedence first, matching the table in SPEC.md §3.1. */
export const POCKET_STATE_ORDER: readonly PocketVisualState[] = [
  'hover-remove',
  'hover-add-new',
  'hover-add-overlap',
  'placed-heavy',
  'placed',
  'placed-light',
  'selected',
  'base',
];

export type PreviewMode = 'add' | 'remove';

/**
 * A transient hover preview. Never mutates the spread — it shows what *would*
 * change. `mode` is `remove` when the hovered bet is already in the spread.
 */
export interface PreviewState {
  readonly mode: PreviewMode;
  readonly pockets: ReadonlySet<PocketId>;
}

export interface PocketStateInput {
  /** Chips currently placed on this pocket. Zero means uncovered. */
  readonly chips: number;
  /** Median chips across covered pockets, for the weight tiers. */
  readonly medianChips: number;
  /** True when a hover preview is touching this pocket. */
  readonly inPreview: boolean;
  readonly previewMode: PreviewMode | null;
  readonly selected: boolean;
}

export type ChipTier = 'heavy' | 'mid' | 'light';

/**
 * Chip-weight tier. Heavy at ≥ 2× the spread median, light at ≤ half it.
 * With a flat spread every pocket sits at the median and reads as `mid`, which
 * is the correct signal: there is no weighting to see.
 */
export function chipTier(chips: number, medianChips: number): ChipTier {
  if (medianChips <= 0) return 'mid';
  if (chips >= medianChips * 2) return 'heavy';
  if (chips <= medianChips / 2) return 'light';
  return 'mid';
}

/**
 * Resolve a pocket to exactly one visual state.
 *
 * Note on precedence: the spec lists `placed` (5) above `placed-light` (6), but
 * `placed` matches every covered pocket, so read literally state 6 could never
 * occur. The three placed states are tiers of one condition, so they are
 * resolved by weight — heavy, light, then mid. See DECISIONS.md D7.
 */
export function resolvePocketState(input: PocketStateInput): PocketVisualState {
  const covered = input.chips > 0;

  if (input.inPreview && input.previewMode === 'remove') return 'hover-remove';
  if (input.inPreview && input.previewMode === 'add') {
    return covered ? 'hover-add-overlap' : 'hover-add-new';
  }

  if (covered) {
    switch (chipTier(input.chips, input.medianChips)) {
      case 'heavy':
        return 'placed-heavy';
      case 'light':
        return 'placed-light';
      case 'mid':
        return 'placed';
    }
  }

  if (input.selected) return 'selected';
  return 'base';
}

/** True for the three states that carry placed coverage underneath. */
export function isPlacedState(state: PocketVisualState): boolean {
  return state === 'placed' || state === 'placed-heavy' || state === 'placed-light';
}

/** §3.1 state 3 renders a stacking dot to mark preview landing on coverage. */
export function showsStackingDot(state: PocketVisualState): boolean {
  return state === 'hover-add-overlap';
}
